/**
 * Phase 5 Advanced AI Features Dashboard
 * Provides unified interface for all Phase 5 AI capabilities
 */

import React, { useState, useEffect, useCallback } from "react";
import "./Phase5Dashboard.css";
import { phase5IntegrationService } from "../services/phase5IntegrationService";
import { machineLearningOptimizationService } from "../services/machineLearningOptimizationService";
import { predictiveAnalyticsEngine } from "../services/predictiveAnalyticsEngine";
import { imageRecognitionService } from "../services/imageRecognitionService";
import { advancedAIFeatureService } from "../services/advancedAIFeatureService";

const Phase5Dashboard = () => {
  const [dashboardData, setDashboardData] = useState(null);
  const [activeTab, setActiveTab] = useState("overview");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [mlInsights, setMlInsights] = useState(null);
  const [predictions, setPredictions] = useState(null);
  const [realtimeUpdates, setRealtimeUpdates] = useState(true);
  const [automationRules, setAutomationRules] = useState([]);

  // Initialize Phase 5 services and dashboard
  useEffect(() => {
    const initializeDashboard = async () => {
      try {
        setLoading(true);

        // Initialize Phase 5 integration service
        const initResult = await phase5IntegrationService.initialize();
        if (!initResult.success) {
          throw new Error("Failed to initialize Phase 5 services");
        }

        // Load initial dashboard data
        const dashData = await phase5IntegrationService.getUnifiedDashboard();
        setDashboardData(dashData);

        // Load ML insights
        const insights =
          await machineLearningOptimizationService.getGuestBehaviorInsights();
        setMlInsights(insights);

        // Load predictions
        const predictions =
          await predictiveAnalyticsEngine.generateSeasonalPredictions();
        setPredictions(predictions);

        // Load automation rules
        const rules = await phase5IntegrationService.getAutomationRules();
        setAutomationRules(rules);

        setError(null);
      } catch (err) {
        console.error("Error initializing Phase 5 dashboard:", err);
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    initializeDashboard();
  }, []);

  // Real-time updates
  useEffect(() => {
    if (!realtimeUpdates) return;

    const interval = setInterval(async () => {
      try {
        const updatedData = await phase5IntegrationService.getRealtimeUpdates();
        if (updatedData.hasChanges) {
          setDashboardData((prev) => ({ ...prev, ...updatedData.data }));
        }
      } catch (err) {
        console.warn("Error fetching real-time updates:", err);
      }
    }, 5000); // Update every 5 seconds

    return () => clearInterval(interval);
  }, [realtimeUpdates]);

  const handleImageUpload = useCallback(async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    try {
      setLoading(true);
      const result = await imageRecognitionService.processWineLabel(file);

      // Update dashboard with new recognition result
      setDashboardData((prev) => ({
        ...prev,
        imageRecognition: {
          ...prev.imageRecognition,
          recentResults: [
            result,
            ...(prev.imageRecognition?.recentResults || []),
          ].slice(0, 10),
        },
      }));
    } catch (err) {
      console.error("Error processing image:", err);
      setError("Failed to process image");
    } finally {
      setLoading(false);
    }
  }, []);

  const generatePredictiveReport = useCallback(async () => {
    try {
      setLoading(true);
      const report =
        await predictiveAnalyticsEngine.generateComprehensiveReport();

      // Update dashboard with new report
      setDashboardData((prev) => ({
        ...prev,
        predictiveAnalytics: {
          ...prev.predictiveAnalytics,
          latestReport: report,
          lastUpdated: new Date().toISOString(),
        },
      }));
    } catch (err) {
      console.error("Error generating predictive report:", err);
      setError("Failed to generate predictive report");
    } finally {
      setLoading(false);
    }
  }, []);

  const optimizeMenuWithAI = useCallback(async () => {
    try {
      setLoading(true);
      const optimization = await advancedAIFeatureService.optimizeMenuWithAI();

      // Update dashboard with optimization results
      setDashboardData((prev) => ({
        ...prev,
        menuOptimization: {
          ...prev.menuOptimization,
          latestOptimization: optimization,
          lastOptimized: new Date().toISOString(),
        },
      }));
    } catch (err) {
      console.error("Error optimizing menu:", err);
      setError("Failed to optimize menu");
    } finally {
      setLoading(false);
    }
  }, []);

  if (loading && !dashboardData) {
    return (
      <div className="phase5-dashboard loading">
        <div className="loading-spinner">
          <div className="spinner"></div>
          <p>Initializing Advanced AI Features...</p>
        </div>
      </div>
    );
  }

  if (error && !dashboardData) {
    return (
      <div className="phase5-dashboard error">
        <div className="error-container">
          <h2>⚠️ Error Loading Phase 5 Dashboard</h2>
          <p>{error}</p>
          <button onClick={() => window.location.reload()}>Retry</button>
        </div>
      </div>
    );
  }

  return (
    <div className="phase5-dashboard">
      <header className="dashboard-header">
        <div className="header-content">
          <h1>🧠 Advanced AI Features Dashboard</h1>
          <p>Machine Learning, Predictive Analytics & Image Recognition</p>
          <div className="header-actions">
            <label className="realtime-toggle">
              <input
                type="checkbox"
                checked={realtimeUpdates}
                onChange={(e) => setRealtimeUpdates(e.target.checked)}
              />{" "}
              Real-time Updates
            </label>
            <div className="service-status">
              {dashboardData?.serviceHealth &&
                Object.entries(dashboardData.serviceHealth).map(
                  ([service, health]) => (
                    <span
                      key={service}
                      className={`status-indicator ${health.status}`}
                    >
                      {service}: {health.status}
                    </span>
                  )
                )}
            </div>
          </div>
        </div>
      </header>

      <nav className="dashboard-tabs">
        <button
          className={activeTab === "overview" ? "active" : ""}
          onClick={() => setActiveTab("overview")}
        >
          📊 Overview
        </button>
        <button
          className={activeTab === "ml-optimization" ? "active" : ""}
          onClick={() => setActiveTab("ml-optimization")}
        >
          🤖 ML Optimization
        </button>
        <button
          className={activeTab === "predictive-analytics" ? "active" : ""}
          onClick={() => setActiveTab("predictive-analytics")}
        >
          📈 Predictive Analytics
        </button>
        <button
          className={activeTab === "image-recognition" ? "active" : ""}
          onClick={() => setActiveTab("image-recognition")}
        >
          📷 Image Recognition
        </button>
        <button
          className={activeTab === "automation" ? "active" : ""}
          onClick={() => setActiveTab("automation")}
        >
          ⚙️ Automation
        </button>
      </nav>

      <main className="dashboard-content">
        {activeTab === "overview" && (
          <div className="overview-tab">
            <div className="overview-grid">
              <div className="metric-card">
                <h3>🎯 ML Accuracy</h3>
                <div className="metric-value">
                  {dashboardData?.mlMetrics?.accuracy || "95.2"}%
                </div>
                <div className="metric-trend positive">+2.3% this week</div>
              </div>

              <div className="metric-card">
                <h3>📊 Predictions Generated</h3>
                <div className="metric-value">
                  {dashboardData?.predictions?.totalGenerated || "1,247"}
                </div>
                <div className="metric-trend positive">+156 today</div>
              </div>

              <div className="metric-card">
                <h3>🖼️ Images Processed</h3>
                <div className="metric-value">
                  {dashboardData?.imageRecognition?.totalProcessed || "342"}
                </div>
                <div className="metric-trend positive">+23 today</div>
              </div>

              <div className="metric-card">
                <h3>💰 Revenue Impact</h3>
                <div className="metric-value">
                  +${dashboardData?.revenueImpact?.total || "15,432"}
                </div>
                <div className="metric-trend positive">
                  From AI optimizations
                </div>
              </div>
            </div>

            <div className="recent-activity">
              <h3>🔄 Recent AI Activity</h3>
              <div className="activity-list">
                {dashboardData?.recentActivity?.map((activity) => (
                  <div
                    key={activity.id || activity.timestamp || Math.random()}
                    className="activity-item"
                  >
                    <span className="activity-icon">{activity.icon}</span>
                    <div className="activity-details">
                      <p>{activity.description}</p>
                      <small>{activity.timestamp}</small>
                    </div>
                  </div>
                )) || <p>No recent activity</p>}
              </div>
            </div>
          </div>
        )}

        {activeTab === "ml-optimization" && (
          <div className="ml-optimization-tab">
            <div className="ml-controls">
              <h3>🤖 Machine Learning Optimization</h3>
              <div className="control-buttons">
                <button
                  onClick={() =>
                    machineLearningOptimizationService.analyzeGuestBehavior()
                  }
                >
                  Analyze Guest Behavior
                </button>
                <button
                  onClick={() =>
                    machineLearningOptimizationService.optimizeSeasonalRecommendations()
                  }
                >
                  Optimize Seasonal Recommendations
                </button>
                <button
                  onClick={() =>
                    machineLearningOptimizationService.analyzeGroupDining()
                  }
                >
                  Analyze Group Dining Patterns
                </button>
              </div>
            </div>

            {mlInsights && (
              <div className="ml-insights">
                <h4>🧠 Recent Insights</h4>
                <div className="insights-grid">
                  <div className="insight-card">
                    <h5>Guest Preferences</h5>
                    <p>
                      Bold reds preferred{" "}
                      {mlInsights.preferences?.boldReds || "68"}% of the time
                    </p>
                  </div>
                  <div className="insight-card">
                    <h5>Seasonal Trends</h5>
                    <p>
                      Sparkling wines up{" "}
                      {mlInsights.seasonal?.sparklingIncrease || "23"}% this
                      season
                    </p>
                  </div>
                  <div className="insight-card">
                    <h5>Price Sensitivity</h5>
                    <p>
                      Sweet spot: ${mlInsights.pricing?.sweetSpot || "45-65"}{" "}
                      range
                    </p>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}

        {activeTab === "predictive-analytics" && (
          <div className="predictive-analytics-tab">
            <div className="analytics-controls">
              <h3>📈 Predictive Analytics</h3>
              <div className="control-buttons">
                <button onClick={generatePredictiveReport}>
                  Generate New Report
                </button>
                <button
                  onClick={() => predictiveAnalyticsEngine.analyzeEventImpact()}
                >
                  Analyze Event Impact
                </button>
                <button
                  onClick={() => predictiveAnalyticsEngine.optimizeInventory()}
                >
                  Optimize Inventory
                </button>
              </div>
            </div>

            {predictions && (
              <div className="predictions-display">
                <h4>🔮 Current Predictions</h4>
                <div className="predictions-grid">
                  {Object.entries(predictions.seasonal || {}).map(
                    ([season, data]) => (
                      <div key={season} className="prediction-card">
                        <h5>
                          {season.charAt(0).toUpperCase() + season.slice(1)}
                        </h5>
                        <div className="prediction-metrics">
                          <div className="metric">
                            <span>Demand:</span>
                            <span className={`trend ${data.trend || "stable"}`}>
                              {data.demandChange || "+5.2"}%
                            </span>
                          </div>
                          <div className="metric">
                            <span>Top Category:</span>
                            <span>{data.topCategory || "Red Wines"}</span>
                          </div>
                        </div>
                      </div>
                    )
                  )}
                </div>
              </div>
            )}
          </div>
        )}

        {activeTab === "image-recognition" && (
          <div className="image-recognition-tab">
            <div className="recognition-controls">
              <h3>📷 Image Recognition</h3>
              <div className="upload-section">
                <label className="upload-button">
                  📷 Upload Wine Label{" "}
                  <input
                    type="file"
                    accept="image/*"
                    onChange={handleImageUpload}
                    style={{ display: "none" }}
                  />
                </label>
                <button
                  onClick={() => imageRecognitionService.processMenuImages()}
                >
                  Process Menu Images
                </button>
                <button
                  onClick={() =>
                    imageRecognitionService.performQualityAssessment()
                  }
                >
                  Quality Assessment
                </button>
              </div>
            </div>

            {dashboardData?.imageRecognition?.recentResults && (
              <div className="recognition-results">
                <h4>🔍 Recent Recognition Results</h4>
                <div className="results-list">
                  {dashboardData.imageRecognition.recentResults.map(
                    (result) => (
                      <div
                        key={result.id || result.timestamp || Math.random()}
                        className="result-item"
                      >
                        <div className="result-image">
                          {result.imageUrl && (
                            <img src={result.imageUrl} alt="Processed" />
                          )}
                        </div>
                        <div className="result-details">
                          <h5>{result.wineName || "Unknown Wine"}</h5>
                          <p>Confidence: {result.confidence || 85}%</p>
                          <p>Vintage: {result.vintage || "Unknown"}</p>
                          <p>Region: {result.region || "Unknown"}</p>
                        </div>
                      </div>
                    )
                  )}
                </div>
              </div>
            )}
          </div>
        )}

        {activeTab === "automation" && (
          <div className="automation-tab">
            <div className="automation-controls">
              <h3>⚙️ AI Automation</h3>
              <div className="control-buttons">
                <button onClick={optimizeMenuWithAI}>
                  Optimize Menu with AI
                </button>
                <button
                  onClick={() =>
                    advancedAIFeatureService.performBatchOptimization()
                  }
                >
                  Batch Optimization
                </button>
                <button
                  onClick={() => phase5IntegrationService.runAutomationRules()}
                >
                  Run Automation Rules
                </button>
              </div>
            </div>

            <div className="automation-rules">
              <h4>📋 Active Automation Rules</h4>
              <div className="rules-list">
                {automationRules.map((rule) => (
                  <div
                    key={rule.id || rule.name || Math.random()}
                    className="rule-item"
                  >
                    <div className="rule-header">
                      <span className="rule-name">{rule.name}</span>
                      <span className={`rule-status ${rule.status}`}>
                        {rule.status}
                      </span>
                    </div>
                    <p className="rule-description">{rule.description}</p>
                    <div className="rule-metrics">
                      <span>Triggers: {rule.triggerCount || 0}</span>
                      <span>Success Rate: {rule.successRate || 95}%</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}
      </main>

      {loading && (
        <div className="loading-overlay">
          <div className="spinner"></div>
        </div>
      )}

      {error && (
        <div className="error-toast">
          <span>⚠️ {error}</span>
          <button onClick={() => setError(null)}>×</button>
        </div>
      )}
    </div>
  );
};

export default Phase5Dashboard;
